import { useEffect, useRef, type MutableRefObject, type RefObject } from 'react';

export type WidgetFetchStatus = 'loading' | 'loaded' | 'error';

/**
 * Dashboard widget retry focus management (`11-dashboard-widget-grid-ux.md §5` "Retry focus
 * management"): after a user-triggered retry resolves, programmatic focus moves to the widget's
 * heading on success so a screen-reader user hears the updated content in context; if the retry
 * fails again, focus stays on the retry button (which the error branch remounts, so we
 * re-focus it explicitly rather than relying on the browser to preserve focus across the
 * loading-skeleton -> error remount).
 *
 * `retriedRef` is a per-widget mutable flag the caller flips to `true` immediately before
 * invoking its own retry callback (see `StatCardWidget`/`ActivityFeedWidget`). Reading it fresh
 * inside the effect — rather than passing a boolean prop — means an unrelated status change
 * (e.g. a *different* stat card triggering the shared summary-group refetch) never steals focus
 * from a widget the user didn't interact with, since only the widget whose own button was
 * clicked ever has its ref set to `true`.
 */
export function useWidgetRetryFocus(
  status: WidgetFetchStatus,
  retriedRef: MutableRefObject<boolean>,
  headingRef: RefObject<HTMLElement>,
  retryButtonRef: RefObject<HTMLElement>,
): void {
  const prevStatusRef = useRef<WidgetFetchStatus>(status);

  useEffect(() => {
    const wasLoading = prevStatusRef.current === 'loading';
    prevStatusRef.current = status;

    if (wasLoading && status !== 'loading' && retriedRef.current) {
      retriedRef.current = false;
      if (status === 'error') {
        retryButtonRef.current?.focus();
      } else {
        headingRef.current?.focus();
      }
    }
  }, [status, retriedRef, headingRef, retryButtonRef]);
}
